import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const foodCategories = ['all', 'protein', 'carbs', 'fats', 'vegetables', 'fruits', 'dairy', 'grains'];

const categoryIcon = {
  protein: 'Beef',
  carbs: 'Wheat',
  fats: 'Droplets',
  vegetables: 'Leaf',
  fruits: 'Apple',
  dairy: 'Milk',
  grains: 'Wheat',
  default: 'UtensilsCrossed',
};

const categoryColor = {
  protein: 'text-red-400 bg-red-400/10',
  carbs: 'text-yellow-400 bg-yellow-400/10',
  fats: 'text-orange-400 bg-orange-400/10',
  vegetables: 'text-green-400 bg-green-400/10',
  fruits: 'text-pink-400 bg-pink-400/10',
  dairy: 'text-blue-400 bg-blue-400/10',
  grains: 'text-amber-400 bg-amber-400/10',
};

const FoodCard = ({ food, onAdd, isAdded }) => (
  <motion.div
    layout
    className={`p-3 rounded-xl border transition-all ${isAdded ? 'border-primary/40 bg-primary/5' : 'border-border bg-card hover:border-primary/30'}`}
    whileHover={{ scale: 1.01 }}
  >
    <div className="flex items-start gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${categoryColor?.[food?.category] || 'text-primary bg-primary/10'}`}>
        <Icon name={categoryIcon?.[food?.category] || categoryIcon?.default} size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-foreground truncate">{food?.name}</span>
          <span className="text-xs text-muted-foreground">{food?.servingSize}</span>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-1">
          <span className="text-xs text-red-400 bg-red-400/10 px-1.5 py-0.5 rounded-full">{food?.calories} kcal</span>
          <span className="text-xs text-blue-400 bg-blue-400/10 px-1.5 py-0.5 rounded-full">P: {food?.protein}g</span>
          <span className="text-xs text-yellow-400 bg-yellow-400/10 px-1.5 py-0.5 rounded-full">C: {food?.carbs}g</span>
          <span className="text-xs text-orange-400 bg-orange-400/10 px-1.5 py-0.5 rounded-full">F: {food?.fats}g</span>
        </div>
      </div>
      <button
        onClick={() => onAdd(food)}
        className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
          isAdded ? 'bg-primary/20 text-primary' : 'bg-primary text-white hover:bg-primary/90'
        }`}
      >
        <Icon name="Plus" size={14} />
      </button>
    </div>
  </motion.div>
);

const NutritionFoodLibraryPanel = ({ foods, customFoods, onAddFood, addedFoodIds, onAddCustomFood }) => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [tab, setTab] = useState('library');
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customForm, setCustomForm] = useState({ name: '', servingSize: '100g', calories: '', protein: '', carbs: '', fats: '', category: 'protein' });

  const filtered = foods?.filter(f => {
    const matchSearch = f?.name?.toLowerCase()?.includes(search?.toLowerCase());
    const matchCat = category === 'all' || f?.category === category;
    return matchSearch && matchCat;
  });

  const filteredCustom = customFoods?.filter(f =>
    f?.name?.toLowerCase()?.includes(search?.toLowerCase())
  );

  const handleCreateCustom = () => {
    if (!customForm?.name?.trim()) return;
    onAddCustomFood({
      ...customForm,
      calories: parseFloat(customForm?.calories) || 0,
      protein: parseFloat(customForm?.protein) || 0,
      carbs: parseFloat(customForm?.carbs) || 0,
      fats: parseFloat(customForm?.fats) || 0,
    });
    setCustomForm({ name: '', servingSize: '100g', calories: '', protein: '', carbs: '', fats: '', category: 'protein' });
    setShowCustomForm(false);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="UtensilsCrossed" size={16} className="text-primary" />
        </div>
        <h2 className="text-base font-bold text-foreground">Food Library</h2>
        <span className="ml-auto text-xs text-muted-foreground">{filtered?.length + filteredCustom?.length} foods</span>
      </div>
      <div className="flex gap-1 mb-3 p-1 bg-muted rounded-xl">
        {['library', 'custom']?.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-all ${tab === t ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}
          >
            {t === 'library' ? 'Library' : `Custom (${customFoods?.length})`}
          </button>
        ))}
      </div>
      <Input
        placeholder="Search foods..."
        value={search}
        onChange={e => setSearch(e?.target?.value)}
        className="mb-3 text-sm"
      />
      {tab === 'library' && (
        <select
          value={category}
          onChange={e => setCategory(e?.target?.value)}
          className="mb-3 w-full px-2 py-1.5 rounded-lg border border-border bg-background text-foreground text-xs"
        >
          {foodCategories?.map(c => <option key={c} value={c}>{c === 'all' ? 'All Categories' : c?.charAt(0)?.toUpperCase() + c?.slice(1)}</option>)}
        </select>
      )}
      {tab === 'custom' && (
        <div className="mb-3">
          <Button size="sm" onClick={() => setShowCustomForm(!showCustomForm)} className="w-full text-xs">
            <Icon name="Plus" size={14} className="mr-1" />
            Add Custom Food
          </Button>
          <AnimatePresence>
            {showCustomForm && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-2 p-3 bg-muted rounded-xl space-y-2">
                  <Input placeholder="Food name *" value={customForm?.name} onChange={e => setCustomForm(p => ({ ...p, name: e?.target?.value }))} className="text-xs" />
                  <Input placeholder="Serving size (e.g. 100g)" value={customForm?.servingSize} onChange={e => setCustomForm(p => ({ ...p, servingSize: e?.target?.value }))} className="text-xs" />
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Calories" type="number" value={customForm?.calories} onChange={e => setCustomForm(p => ({ ...p, calories: e?.target?.value }))} className="text-xs" />
                    <Input placeholder="Protein (g)" type="number" value={customForm?.protein} onChange={e => setCustomForm(p => ({ ...p, protein: e?.target?.value }))} className="text-xs" />
                    <Input placeholder="Carbs (g)" type="number" value={customForm?.carbs} onChange={e => setCustomForm(p => ({ ...p, carbs: e?.target?.value }))} className="text-xs" />
                    <Input placeholder="Fats (g)" type="number" value={customForm?.fats} onChange={e => setCustomForm(p => ({ ...p, fats: e?.target?.value }))} className="text-xs" />
                  </div>
                  <select value={customForm?.category} onChange={e => setCustomForm(p => ({ ...p, category: e?.target?.value }))} className="w-full px-2 py-1.5 rounded-lg border border-border bg-background text-foreground text-xs">
                    {foodCategories?.filter(c => c !== 'all')?.map(c => <option key={c} value={c}>{c?.charAt(0)?.toUpperCase() + c?.slice(1)}</option>)}
                  </select>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleCreateCustom} className="flex-1 text-xs">Create</Button>
                    <Button size="sm" variant="outline" onClick={() => setShowCustomForm(false)} className="flex-1 text-xs">Cancel</Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
      <div className="flex-1 overflow-y-auto space-y-2 pr-1">
        {tab === 'library' && filtered?.map(food => (
          <FoodCard key={food?.id} food={food} onAdd={onAddFood} isAdded={addedFoodIds?.includes(food?.id)} />
        ))}
        {tab === 'custom' && filteredCustom?.map(food => (
          <FoodCard key={food?.id} food={food} onAdd={onAddFood} isAdded={addedFoodIds?.includes(food?.id)} />
        ))}
        {tab === 'library' && filtered?.length === 0 && (
          <div className="text-center py-8 text-muted-foreground text-sm">No foods match your filters</div>
        )}
        {tab === 'custom' && filteredCustom?.length === 0 && !showCustomForm && (
          <div className="text-center py-8 text-muted-foreground text-sm">No custom foods yet</div>
        )}
      </div>
    </div>
  );
};

export default NutritionFoodLibraryPanel;
